'use client';

import {
	BigNum,
	BN,
	VelocityClient,
	getUserStatsAccountPublicKey,
	PollingUserAccountSubscriber,
	PublicKey,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	UserAccount,
	UserStatsAccount,
	ZERO,
	User,
	BulkAccountLoader,
} from '@velocity-exchange/sdk';
import {
	Vault,
	VAULT_SHARES_PRECISION_EXP,
	VaultClient,
} from '@velocity-exchange/vaults-sdk';
import {
	MarketId,
	UIMarket,
	getMultipleAccountsInfoChunked,
} from '@velocity-exchange/common';
import { Connection } from '@solana/web3.js';
import chunk from 'lodash/chunk';
import { ApyReturnsLookup, VaultStats } from 'src/@types/vaults';
import { DEFAULT_OFF_CHAIN_STATS } from 'src/constants/vaults/misc';
import {
	userAccountDataDecoder,
	userStatsDataDecoder,
	vaultDataDecoder,
} from 'src/utils/programAccounts/decoders';
import { getVaultConfigCached } from 'src/utils/vault/configCache';
import { dlog } from '../../dev';

export type VaultsOnChainDataLookup = Record<
	string,
	{
		vaultAccountData: Vault;
		userStatsData: UserStatsAccount;
		vaultQuoteTvl: BN;
		vaultVelocityUser: UserAccount;
	}
>;

// ⭐️⭐️⭐️ NOTE FOR NULLCHECK FIXORS ⭐️⭐️⭐️ :: Commit c0c8d4fc5dfc72ca7604ce995695872f55bbd5de which tried fixing nullcheck stuff in this file caused the syncing to break .. I have reverted this for now, I can probably return to try fix the nullcheck stuff again later but for now is not a priority.

// this method of processing is used to reduce the number of individual RPC calls needed to
// be made to get a vault's on-chain data, by combining most accounts into one `getMultipleAccountsInfo` RPC call
export const getMultipleOnChainVaultData = async (
	velocityClient: VelocityClient,
	vaultClient: VaultClient,
	connection: Connection,
	bulkAccountLoader: BulkAccountLoader,
	vaultPubKeys: PublicKey[]
): Promise<VaultsOnChainDataLookup> => {
	dlog(`vaults_debugging`, `fetching_vault_accounts`, {
		vaultPubKeys,
	});

	// create pairs of vault public keys and its user stats account
	const pubKeysToFetch = vaultPubKeys
		.map((vaultPubKey) => [
			vaultPubKey,
			getUserStatsAccountPublicKey(
				velocityClient.program.programId,
				vaultPubKey
			),
			new PublicKey(
				getVaultConfigCached(vaultPubKey.toString())
					?.velocityUserPubkeyString ?? 0
			),
		])
		.flat();

	const chunks = chunk(pubKeysToFetch, 100); // getMultipleAccountsInfo has a limit of 100 accounts per call
	const responses = await Promise.all(
		chunks.map((pubKeyChunk) => connection.getMultipleAccountsInfo(pubKeyChunk))
	);

	const accountsFetched = responses.flat();

	const vaultsOnChainDataLookup: VaultsOnChainDataLookup = {};

	// process each pair of data
	for (let i = 0; i < vaultPubKeys.length; i++) {
		const responseIndex = i * 3;
		const vaultAccountBuffer = accountsFetched[responseIndex]?.data;
		const userStatsBuffer = accountsFetched[responseIndex + 1]?.data;
		const velocityUserAccountBuffer = accountsFetched[responseIndex + 2]?.data;

		const vaultAccountData = vaultDataDecoder(
			vaultAccountBuffer ?? Buffer.alloc(0),
			// @ts-ignore
			vaultClient.program
		);
		const userStatsData = userStatsDataDecoder(
			userStatsBuffer ?? Buffer.alloc(0),
			velocityClient.program
		);
		const velocityUserAccountData: UserAccount = userAccountDataDecoder(
			velocityUserAccountBuffer ?? Buffer.alloc(0),
			velocityClient.program
		);

		const vaultPubKeyString = vaultPubKeys[i].toString();
		vaultsOnChainDataLookup[vaultPubKeyString] = {
			vaultAccountData,
			userStatsData,
			vaultQuoteTvl: ZERO,
			vaultVelocityUser: velocityUserAccountData,
		};
	}

	// instead of fetching each vault individually and calculating the net usd value of the vault,
	// we do this workaround by
	// 1. fetching all the vault user accounts in one go
	// 2. assign the vault user account data to PollingUserAccountSubscriber
	// 3. create the User client and assign the PollingUserAccountSubscriber to it
	// 4. calculate the net usd value of the vault

	const vaultUserAccountData = await getMultipleAccountsInfoChunked(
		connection,
		vaultPubKeys.map(
			(vaultPubKey) =>
				vaultsOnChainDataLookup[vaultPubKey.toString()].vaultAccountData.user
		)
	);

	// Decode via the program's IDL-agnostic accounts coder directly, rather than
	// through `program.account.user` — the account namespace is the deep,
	// IDL-invariant part of the generic that trips TS2589 on our large IDL.
	const decoder = (name: string, buffer: Buffer): UserAccount =>
		velocityClient.program.coder.accounts.decodeUnchecked(name, buffer);

	const vaultQuoteValues = vaultUserAccountData.map(
		(userAccountData, index) => {
			if (!userAccountData?.data) {
				return ZERO;
			}

			const userAccountPublicKey =
				vaultsOnChainDataLookup[vaultPubKeys[index].toString()].vaultAccountData
					.user;

			const pollingUserAccountSubscriber = new PollingUserAccountSubscriber(
				connection,
				userAccountPublicKey,
				bulkAccountLoader,
				decoder
			);

			const decodedUserAccount = decoder('user', userAccountData?.data);

			pollingUserAccountSubscriber.updateData(decodedUserAccount, 10); // 10 is just an arbitrary slot number to ensure its bigger than undefined/0

			const user = new User({
				velocityClient: velocityClient,
				userAccountPublicKey,
				accountSubscription: {
					type: 'polling',
					accountLoader: bulkAccountLoader,
				},
			});
			user.accountSubscriber = pollingUserAccountSubscriber;
			const netUsdValue = user.getNetUsdValue();

			return netUsdValue;
		}
	);

	vaultQuoteValues.forEach((vaultQuoteValue, index) => {
		vaultsOnChainDataLookup[vaultPubKeys[index].toString()].vaultQuoteTvl =
			vaultQuoteValue;
	});

	return vaultsOnChainDataLookup;
};

const getMetaDataForVault = (
	vaultOnChainData: VaultsOnChainDataLookup[string]
): VaultStats['metadata'] => {
	return {
		activePerpMarketIndexes: vaultOnChainData.vaultVelocityUser.perpPositions
			.filter((position) => position.baseAssetAmount.abs().gtn(0)) // Doing abs() because short positions will be negative
			.map((position) => position.marketIndex),
		activeSpotMarketIndexes: vaultOnChainData.vaultVelocityUser.spotPositions
			.filter((position) => position.scaledBalance.abs().gtn(0)) // Doing abs() because borrow positions will be negative
			.map((position) => position.marketIndex),
	};
};

export function constructVaultStats(
	vaultPubKey: string,
	vaultOnChainData: VaultsOnChainDataLookup[string],
	apyReturnStat: ApyReturnsLookup[string] | undefined,
	oraclePriceGetter: (marketId: MarketId | undefined) => BigNum | undefined
): VaultStats {
	const uiVaultConfig = getVaultConfigCached(vaultPubKey);
	const uiMarket = UIMarket.createSpotMarket(uiVaultConfig?.depositAsset ?? 0);
	const marketConfig = uiMarket.market as SpotMarketConfig;
	const oraclePriceBigNum = oraclePriceGetter(uiMarket.marketId);

	const offChainStats = apyReturnStat
		? { ...apyReturnStat, hasLoadedOffChainStats: true }
		: DEFAULT_OFF_CHAIN_STATS;

	if (oraclePriceBigNum?.eqZero()) {
		return {
			vaultAccount: vaultOnChainData.vaultAccountData,
			vaultUserStatsAccount: vaultOnChainData.userStatsData,
			isOnChainStatsLoaded: false,
			totalBasePnl: BigNum.from(ZERO, marketConfig.precisionExp),
			totalQuotePnl: BigNum.from(ZERO, QUOTE_PRECISION_EXP),
			tvlBase: BigNum.from(ZERO, marketConfig.precisionExp),
			tvlQuote: BigNum.from(ZERO, QUOTE_PRECISION_EXP),
			capacityPct: 0,
			volume30Days: BigNum.from(ZERO, QUOTE_PRECISION_EXP),
			isUncappedCapacity: false,
			totalShares: BigNum.from(ZERO, VAULT_SHARES_PRECISION_EXP),
			vaultRedeemPeriodSecs: ZERO,
			notionalGrowthQuotePnl: BigNum.from(ZERO, QUOTE_PRECISION_EXP),
			hasLoadedOnChainStats: false,
			profitShare: 0,
			metadata: getMetaDataForVault(vaultOnChainData),
			hasSyncedWithOraclePrice: false,
			...offChainStats,
		};
	}

	const vaultAccountData = vaultOnChainData.vaultAccountData;
	const userStats = vaultOnChainData.userStatsData;

	const vaultQuoteTvl = BigNum.from(
		vaultOnChainData.vaultQuoteTvl,
		QUOTE_PRECISION_EXP
	);
	const vaultBaseTvl = vaultQuoteTvl
		.shift(marketConfig.precisionExp)
		.div(oraclePriceBigNum ?? ZERO);
	const totalBasePnl = vaultBaseTvl.sub(
		BigNum.from(vaultAccountData.netDeposits, marketConfig.precisionExp)
	);
	const totalQuotePnl = totalBasePnl
		.mul(oraclePriceBigNum ?? ZERO)
		.shiftTo(QUOTE_PRECISION_EXP);
	const capacityPct = vaultAccountData.maxTokens.eqn(0) // uncapped capacity
		? '0'
		: vaultBaseTvl.toPercentage(
				BigNum.from(vaultAccountData.maxTokens, marketConfig.precisionExp),
				marketConfig.precisionExp.toNumber()
			);

	const volume30Days = BigNum.from(
		userStats.makerVolume30D.add(userStats.takerVolume30D),
		QUOTE_PRECISION_EXP
	);

	const vaultVelocityUserQuoteNetDeposits = BigNum.from(
		vaultOnChainData.vaultVelocityUser.totalDeposits.sub(
			vaultOnChainData.vaultVelocityUser.totalWithdraws
		),
		QUOTE_PRECISION_EXP
	);
	const notionalGrowthQuotePnl = vaultQuoteTvl.sub(
		vaultVelocityUserQuoteNetDeposits
	);

	return {
		vaultAccount: vaultAccountData,
		vaultUserStatsAccount: vaultOnChainData.userStatsData,
		isOnChainStatsLoaded: true,
		totalBasePnl,
		totalQuotePnl,
		tvlBase: vaultBaseTvl,
		tvlQuote: vaultQuoteTvl,
		capacityPct: Math.min(+capacityPct, 100),
		isUncappedCapacity: vaultAccountData.maxTokens.eqn(0),
		volume30Days,
		totalShares: BigNum.from(
			vaultAccountData.totalShares,
			VAULT_SHARES_PRECISION_EXP
		),
		vaultRedeemPeriodSecs: vaultAccountData.redeemPeriod,
		notionalGrowthQuotePnl,
		hasLoadedOnChainStats: true,
		profitShare: vaultAccountData.profitShare,
		metadata: getMetaDataForVault(vaultOnChainData),
		hasSyncedWithOraclePrice: true,
		...offChainStats,
	};
}
